import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import BuildingDetailsModal from "./BuildingDetailsModal";
import LodgeDetailsModal from "./LodgeDetailsModal";
import MemberDetailsModal from "./MemberDetailsModal";
import MeetingDetailsModal from "./MeetingDetailsModal";
import HighlightsModal from "./HighlightsModal";
import CharitiesManager from "./CharitiesManager";
import OffCatalogueModal from "./OffCatalogueModal";
import "./ResourcesPage.css";

function ResourcesPage({ user, openLoginModal }) {
    const navigate = useNavigate();
    const userRole = user?.role || "Guest";

    const [modalOpen, setModalOpen] = useState(false);
    const [currentModal, setCurrentModal] = useState(null);
    const [query, setQuery] = useState("");
    const [loadingResource, setLoadingResource] = useState("");

    const [selectedBuilding, setSelectedBuilding] = useState(null);
    const [selectedLodge, setSelectedLodge] = useState(null);
    const [selectedMember, setSelectedMember] = useState(null);
    const [selectedMeeting, setSelectedMeeting] = useState(null);
    const [highlightYear, setHighlightYear] = useState(null);

    const [resourceStats, setResourceStats] = useState({
        temples: null,
        lodges: null,
        members: null,
        meetings: null,
        charities: null,
        cartoons: null,
    });

    useEffect(() => {
        let cancelled = false;
        const countArray = (value) => Array.isArray(value) ? value.length : null;

        const loadStats = async () => {
            const [buildings, lodges, membersCount, meetings, charities, cartoons] = await Promise.allSettled([
                axios.get("/api/buildings"),
                axios.get("/api/resources/lodges"),
                axios.get("/api/resources/members/count"),
                axios.get("/api/meetings"),
                axios.get("/api/resources/charities"),
                axios.get("/api/resources/off-the-catalogue"),
            ]);

            if (cancelled) return;

            setResourceStats({
                temples: buildings.status === "fulfilled" ? countArray(buildings.value.data) : null,
                lodges: lodges.status === "fulfilled" ? countArray(lodges.value.data) : null,
                members: membersCount.status === "fulfilled"
                    ? Number(membersCount.value.data?.count ?? membersCount.value.data?.total ?? 0)
                    : null,
                meetings: meetings.status === "fulfilled" ? countArray(meetings.value.data) : null,
                charities: charities.status === "fulfilled" ? countArray(charities.value.data) : null,
                cartoons: cartoons.status === "fulfilled" ? countArray(cartoons.value.data) : 1,
            });
        };

        loadStats().catch((error) => console.error("Error loading resource stats:", error));
        return () => {
            cancelled = true;
        };
    }, []);

    const canUse = useCallback((roles) => Boolean(user && roles.includes(user.role)), [user]);

    const updateCartoonCount = useCallback((count) => {
        setResourceStats((current) => ({ ...current, cartoons: count }));
    }, []);

    const withLoading = async (key, task) => {
        setLoadingResource(key);
        try {
            await task();
        } finally {
            setLoadingResource("");
        }
    };

    const sections = useMemo(() => [
        {
            name: "Off the Catalogue",
            icon: "/images/off-the-catalogue.webp",
            category: "Cartoon series",
            description: "Curious stories from the Connaught 361 archives, told through one-panel cartoons with more questions than answers.",
            metricKey: "cartoons",
            metricLabel: "Panels",
            accessLabel: "Browse",
            cardClass: "resources-card-cartoon",
            allowedRoles: ["Admin", "Archivist", "Researcher", "Member", "Secretary", "Almoner"],
            action: () => {
                if (!user) return openLoginModal();
                setCurrentModal("off-catalogue");
                setModalOpen(true);
            },
        },
        {
            name: "Timeline Builder",
            icon: "/images/highlights.png",
            category: "Archive tools",
            description: "Create, edit, draft, publish, feature, and source entries for the public interactive timeline.",
            metricLabel: "Draft / publish",
            allowedRoles: ["Admin", "Archivist", "Researcher", "Secretary"],
            action: () => {
                if (!canUse(["Admin", "Archivist", "Researcher", "Secretary"])) return openLoginModal();
                setHighlightYear(null);
                setCurrentModal("highlights");
                setModalOpen(true);
            },
        },
        {
            name: "Research Papers",
            icon: "/images/research.png",
            category: "Research",
            description: "Draft Connaught 361 paper issues, edit LaTeX sections, and build private PDFs.",
            metricLabel: "Builder",
            allowedRoles: ["Admin", "Archivist", "Researcher", "Secretary"],
            action: () => {
                if (!canUse(["Admin", "Archivist", "Researcher", "Secretary"])) return openLoginModal();
                navigate("/research-papers");
            },
        },
        {
            name: "Games",
            icon: "/images/games.svg",
            category: "Games",
            description: "Explore The Lost Jewel and The Lodge: two adventures in fellowship, humour and ceremonial preparation.",
            metricLabel: "Games",
            metricValue: "2",
            accessLabel: "Preview",
            developmentBanner: "Games in development",
            cardClass: "resources-card-game",
            allowedRoles: ["Admin", "Archivist", "Researcher", "Member", "Secretary", "Almoner"],
            action: () => {
                if (!user) return openLoginModal();
                navigate("/games");
            },
        },
        {
            name: "Temples",
            icon: "/images/buildings.png",
            category: "Places",
            description: "Buildings, temple details, locations, and movement history.",
            metricKey: "temples",
            metricLabel: "Records",
            allowedRoles: ["Admin", "Archivist", "Researcher", "Secretary"],
            action: () => {
                if (!canUse(["Admin", "Archivist", "Researcher", "Secretary"])) return openLoginModal();
                return withLoading("Temples", async () => {
                    const response = await axios.get("/api/buildings");
                    setSelectedBuilding(response.data);
                    setCurrentModal("building");
                    setModalOpen(true);
                }).catch((error) => console.error("Error fetching buildings:", error));
            },
        },
        {
            name: "Lodges",
            icon: "/images/lodges.png",
            category: "Lodges",
            description: "Lodge registers, warrants, constitutions, status, and location links.",
            metricKey: "lodges",
            metricLabel: "Records",
            allowedRoles: ["Admin", "Archivist", "Researcher", "Secretary"],
            action: () => {
                if (!canUse(["Admin", "Archivist", "Researcher", "Secretary"])) return openLoginModal();
                return withLoading("Lodges", async () => {
                    const response = await axios.get("/api/resources/lodges");
                    setSelectedLodge(response.data);
                    setCurrentModal("lodge");
                    setModalOpen(true);
                }).catch((error) => console.error("Error fetching lodges:", error));
            },
        },
        {
            name: "Members",
            icon: "/images/members.png",
            category: "People",
            description: "Member profiles, membership histories, degrees, and officer records.",
            metricKey: "members",
            metricLabel: "Records",
            allowedRoles: ["Admin", "Archivist", "Researcher", "Member", "Secretary"],
            action: () => {
                if (!canUse(["Admin", "Archivist", "Researcher", "Member", "Secretary"])) return openLoginModal();
                return withLoading("Members", async () => {
                    const response = await axios.get("/api/resources/members");
                    setSelectedMember(response.data);
                    setCurrentModal("member");
                    setModalOpen(true);
                }).catch((error) => console.error("Error fetching members:", error));
            },
        },
        {
            name: "Meetings",
            icon: "/images/meetings.png",
            category: "Activity",
            description: "Meeting records, attendance, event types, and linked documents.",
            metricKey: "meetings",
            metricLabel: "Records",
            allowedRoles: ["Admin", "Archivist", "Researcher", "Secretary"],
            action: () => {
                if (!canUse(["Admin", "Archivist", "Researcher", "Secretary"])) return openLoginModal();
                return withLoading("Meetings", async () => {
                    const response = await axios.get("/api/meetings");
                    setSelectedMeeting(response.data);
                    setCurrentModal("meeting");
                    setModalOpen(true);
                }).catch((error) => console.error("Error fetching meetings:", error));
            },
        },
        {
            name: "Charities & Funds",
            icon: "/images/charity.svg",
            category: "Funds",
            description: "Charity records, fund links, donations, and lodge associations.",
            metricKey: "charities",
            metricLabel: "Records",
            allowedRoles: ["Admin", "Archivist", "Researcher", "Secretary"],
            action: () => {
                if (!canUse(["Admin", "Archivist", "Researcher", "Secretary"])) return openLoginModal();
                setCurrentModal("charities");
                setModalOpen(true);
            },
        },
    ], [canUse, navigate, openLoginModal, user]);

    const visibleSections = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return sections;
        return sections.filter((section) =>
            [section.name, section.category, section.description]
                .some((value) => value.toLowerCase().includes(needle))
        );
    }, [query, sections]);

    const accessibleCount = sections.filter((section) => canUse(section.allowedRoles)).length;
    const totalCount = sections.length;

    const handleSectionClick = (section) => {
        if (section.route) {
            if (canUse(section.allowedRoles)) navigate(section.route);
            else openLoginModal();
            return;
        }

        section.action?.();
    };

    const handleSectionKeyDown = (event, section) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleSectionClick(section);
        }
    };

    const formatMetric = (section) => {
        if (section.metricValue) return section.metricValue;
        if (!section.metricKey) return "Open";
        const value = resourceStats[section.metricKey];
        return value == null ? "..." : value.toLocaleString();
    };

    return (
        <div className="resources-page-container">
            <section className="resources-hero">
                <div>
                    <span className="resources-eyebrow">Connaught Research Lodge</span>
                    <h1>Resources</h1>
                    <p>
                        Explore the operational archive: places, lodges, members, meetings, analysis, and charitable funds.
                    </p>
                </div>
                <div className="resources-hero-stats" aria-label="Resource access summary">
                    <div>
                        <strong>{accessibleCount}</strong>
                        <span>available</span>
                    </div>
                    <div>
                        <strong>{totalCount}</strong>
                        <span>modules</span>
                    </div>
                    <div>
                        <strong>{userRole}</strong>
                        <span>role</span>
                    </div>
                </div>
            </section>

            <section className="resources-toolbar" aria-label="Resource controls">
                <div className="resources-search">
                    <label htmlFor="resources-search-input">Find resource</label>
                    <input
                        id="resources-search-input"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search modules, records, people, meetings..."
                    />
                </div>
                <div className="resources-access-note">
                    {user ? `${accessibleCount} of ${totalCount} modules available` : "Sign in to unlock member resources"}
                </div>
            </section>

            <section className="resources-grid">
                {visibleSections.map((section) => {
                    const hasAccess = canUse(section.allowedRoles);
                    return (
                        <article
                            key={section.name}
                            className={`resources-card ${section.cardClass || ""} ${hasAccess ? "" : "resources-card-disabled"}`}
                            onClick={() => handleSectionClick(section)}
                            onKeyDown={(event) => handleSectionKeyDown(event, section)}
                            role="button"
                            tabIndex={0}
                            aria-label={`Open ${section.name}`}
                        >
                            <div className="resources-card-top">
                                <span className="resources-category">{section.category}</span>
                                {section.developmentBanner && (
                                    <span className="resources-development-banner">{section.developmentBanner}</span>
                                )}
                                <span className={`resources-access ${hasAccess ? "open" : "locked"}`}>
                                    {hasAccess ? (section.accessLabel || "Open") : "Locked"}
                                </span>
                            </div>
                            <div className="resources-card-main">
                                <img src={section.icon} alt="" aria-hidden="true" className="resources-card-icon" />
                                <div>
                                    <h3 className="resources-card-title">{section.name}</h3>
                                    <p>{section.description}</p>
                                </div>
                            </div>
                            <div className="resources-card-footer">
                                <span>{section.metricLabel}</span>
                                <strong>{loadingResource === section.name ? "Loading" : formatMetric(section)}</strong>
                            </div>
                        </article>
                    );
                })}
            </section>

            {visibleSections.length === 0 && (
                <div className="resources-empty">
                    <h3>No matching resources</h3>
                    <p>Try a broader search term.</p>
                </div>
            )}

            {modalOpen && currentModal === "building" && selectedBuilding && (
                <BuildingDetailsModal
                    buildingData={selectedBuilding}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedBuilding(null);
                    }}
                />
            )}

            {modalOpen && currentModal === "lodge" && selectedLodge && (
                <LodgeDetailsModal
                    lodgeData={selectedLodge}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedLodge(null);
                    }}
                />
            )}

            {modalOpen && currentModal === "member" && selectedMember && (
                <MemberDetailsModal
                    memberData={selectedMember}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedMember(null);
                    }}
                />
            )}

            {modalOpen && currentModal === "meeting" && selectedMeeting && (
                <MeetingDetailsModal
                    meetingData={selectedMeeting}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedMeeting(null);
                    }}
                />
            )}

            {modalOpen && currentModal === "highlights" && (
                <HighlightsModal
                    year={highlightYear}
                    user={user}
                    onClose={() => {
                        setModalOpen(false);
                        setHighlightYear(null);
                    }}
                />
            )}

            {modalOpen && currentModal === "charities" && (
                <CharitiesManager onClose={() => setModalOpen(false)} />
            )}

            {modalOpen && currentModal === "off-catalogue" && (
                <OffCatalogueModal
                    user={user}
                    onCountChange={updateCartoonCount}
                    onClose={() => setModalOpen(false)}
                />
            )}
        </div>
    );
}

export default ResourcesPage;
